import { useState } from 'react';
import { BrandMark } from '../../components/BrandMark';
import type { FormEvent } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api';
import { useResource } from '../../hooks';
import { staffApi } from '../../staffApi';
import type { StaffSession } from '../../staffApi';
import { AuthCardSkeleton, ErrorNotice, Field, PasswordInput, Skeleton, ThemeToggle } from '../../components/ui';
import { StaffActivityPage } from './StaffActivityPage';
import { StaffOrganizationPage } from './StaffOrganizationPage';
import { Callout, Input, NotFound } from '../../components/primitives';
import { StaffOrganizationsPage } from './StaffOrganizationsPage';
import { StaffPlatformPanel } from './StaffPlatformPanel';

/**
 * The staff console: Hestaworks seeing every organization Commerce Ops
 * serves (docs/adr/0016-staff-console-and-addons.md). It shares the
 * dashboard's tokens and components, but never its session — and its dark
 * header band is there so nobody mistakes it for a client's workspace.
 */
export function StaffApp() {
  return (
    <Routes>
      <Route path="sign-in" element={<StaffSignInPage />} />
      <Route path="*" element={<StaffGate />} />
    </Routes>
  );
}

function needsStaffSignIn(error: Error | null): boolean {
  return (
    error instanceof ApiError && (error.code === 'staff_not_authenticated' || error.code === 'staff_session_invalid')
  );
}

function StaffGate() {
  const [attempt, setAttempt] = useState(0);
  const session = useResource(() => staffApi.session(), [attempt]);
  const location = useLocation();

  if (session.loading && !session.data) return <StaffShellSkeleton />;

  if (needsStaffSignIn(session.error)) {
    const destination = `${location.pathname}${location.search}`;
    const next = destination === '/staff' ? '' : `?next=${encodeURIComponent(destination)}`;
    return <Navigate to={`/staff/sign-in${next}`} replace />;
  }

  if (session.error || !session.data) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__brand">
            <BrandMark size={26} />
            Commerce Ops staff
          </div>
          <ErrorNotice error={session.error} />
          <button className="button" type="button" onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return <StaffShell session={session.data} onSignedOut={() => setAttempt((value) => value + 1)} />;
}

function StaffNav() {
  const location = useLocation();
  const onActivity = location.pathname.startsWith('/staff/activity');
  return (
    <nav className="staff-bar__nav" aria-label="Staff console">
      <Link to="/staff" className={onActivity ? undefined : 'active'} aria-current={onActivity ? undefined : 'page'}>
        Organizations
      </Link>
      <Link to="/staff/activity" className={onActivity ? 'active' : undefined} aria-current={onActivity ? 'page' : undefined}>
        Staff activity
      </Link>
    </nav>
  );
}

function StaffShell({ session, onSignedOut }: { session: StaffSession; onSignedOut: () => void }) {
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function signOut() {
    setSigningOut(true);
    setError(null);
    try {
      await staffApi.logout();
      onSignedOut();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setSigningOut(false);
    }
  }

  return (
    <div className="app-shell staff-shell">
      <header className="staff-bar">
        <div className="staff-bar__inner">
          <div className="staff-bar__left">
            <Link to="/staff" className="staff-bar__brand">
              <BrandMark />
              <span>Commerce Ops</span>
            </Link>
            <span className="staff-bar__scope">Staff console</span>
            <StaffNav />
          </div>
          <div className="staff-bar__right">
            <ThemeToggle />
            <span className="staff-bar__who" title={session.staff.email}>
              {session.staff.name}
            </span>
            <button className="staff-bar__signout" type="button" onClick={signOut} disabled={signingOut}>
              {signingOut ? 'Signing out' : 'Sign out'}
            </button>
          </div>
        </div>
      </header>
      <div className="app">
        <main className="app__main staff-main">
          {error ? <ErrorNotice error={error} /> : null}
          <Routes>
            {/* The platform's safe-mode status and controls belong to the organizations page (CO-184, OR22). */}
            <Route
              path="/"
              element={
                <>
                  <StaffPlatformPanel />
                  <StaffOrganizationsPage addons={session.addons} />
                </>
              }
            />
            <Route
              path="/organizations/:organizationId"
              element={
                <>
                  <StaffPlatformPanel compact />
                  <StaffOrganizationPage addons={session.addons} />
                </>
              }
            />
            <Route
              path="/activity"
              element={
                <>
                  <StaffPlatformPanel compact />
                  <StaffActivityPage addons={session.addons} />
                </>
              }
            />
            <Route path="*" element={<NotFound home="/staff" homeLabel="Go to organizations" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function StaffShellSkeleton() {
  return (
    <div className="app-shell staff-shell" role="status" aria-label="Loading the staff console">
      <header className="staff-bar">
        <div className="staff-bar__inner">
          <div className="staff-bar__left">
            <div className="staff-bar__brand">
              <BrandMark />
              <span>Commerce Ops</span>
            </div>
            <span className="staff-bar__scope">Staff console</span>
          </div>
        </div>
      </header>
      <div className="app">
        <main className="app__main staff-main">
          <StaffTableSkeleton />
        </main>
      </div>
    </div>
  );
}

/** StaffTableSkeleton is shaped like the organization list: a title, the
 * filter row, then table rows. */
export function StaffTableSkeleton({ withHeader = true }: { withHeader?: boolean }) {
  return (
    <div className="skeleton-page" aria-hidden="true">
      {withHeader ? (
        <>
          <Skeleton width="220px" height="28px" />
          <div className="staff-filters">
            <Skeleton width="100%" height="54px" />
            <Skeleton width="100%" height="54px" />
            <Skeleton width="100%" height="54px" />
            <Skeleton width="100%" height="54px" />
          </div>
        </>
      ) : null}
      <div className="skeleton-page__rows">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} width="100%" height="52px" radius="var(--radius-sm)" />
        ))}
      </div>
    </div>
  );
}

function StaffSignInPage() {
  const existing = useResource(() => staffApi.session(), []);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const next = params.get('next');
  const destination = next && next.startsWith('/staff') && !next.startsWith('//') ? next : '/staff';

  if (existing.loading) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <AuthCardSkeleton />
        </div>
      </div>
    );
  }
  if (existing.data) return <Navigate to={destination} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await staffApi.login(email, password);
      navigate(destination, { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setBusy(false);
    }
  }

  const refused = error instanceof ApiError && error.code === 'invalid_credentials';

  return (
    <div className="auth-shell staff-auth">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <div className="staff-auth__band" aria-hidden="true" />
        <div className="auth-card__brand">
          <BrandMark size={26} />
          Commerce Ops
        </div>
        <h1>Staff sign-in</h1>
        <p className="type-description">
          Client accounts sign in at <Link to="/login">the client sign-in page</Link>.
        </p>
        <form className="form" onSubmit={submit} noValidate={false}>
          <Field label="Email">
            <Input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              spellCheck={false}
              disabled={busy}
            />
          </Field>
          <Field label="Password">
            <PasswordInput
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </Field>
          {refused ? (
            <Callout tone="danger" title="Email or password is incorrect.">
              Staff accounts are created with staffctl; a client login does not work here.
            </Callout>
          ) : (
            <ErrorNotice error={error} />
          )}
          <button className="button" type="submit" disabled={busy}>
            {busy ? 'Signing in' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
